/**
 * A slot drill-down's reveal-as-you-drag (INS-12796): which bars are slots, how the capped chart and
 * the slot list highest to lowest combine as the chart is dragged, and the keys, status and notice
 * a slot level reads. The capped chart and the list come from two endpoints; the reconciling
 * between them lives here.
 */
import { format0 } from 'utils/number';
import { isSlotKey, rescatOption, toSlotRows } from './utils';
import type {
  EmptyNotice,
  RevealedSlotWaterfall,
  SlotListOrder,
  SlotRowsPageResponse,
  WaterfallBar,
} from './types';

/** The key of a slot drill-down's aggregate bar: the slots it doesn't draw as bars of their own. */
export const OTHER_SLOTS_KEY = 'other_slots';

/** The results' switch that shows the slots that didn't change — the empty states name it. */
export const SHOW_UNCHANGED_SLOTS_LABEL = 'Show $0 slots';

/** The overview's two fixed slot factors, which drill straight to their slots. */
const SLOT_COVERAGE_KEYS = new Set(['slots_removed', 'slots_added']);

/**
 * Whether a key names one of the overview's two fixed slot factors — Slots Removed or Slots
 * Added — whose drill-down is a slot level, not a group of factors.
 *
 * @param {string} key - An overview bar's (or group's) key.
 * @return {boolean} True for `slots_removed` / `slots_added`.
 */
export function isSlotCoverageKey(key: string): boolean {
  return SLOT_COVERAGE_KEYS.has(key);
}

/** How many more slots each drag past a slot drill-down's end reveals. */
export const SLOT_REVEAL_STEP = 10;

/**
 * How far ($) the slots drawn may fall short of the capped chart's total before the shortfall
 * keeps a bar of its own even when every slot is drawn — so the bars always close on the anchor.
 */
const SLOT_RESIDUAL_TOLERANCE = 1;

/**
 * Whether a drill-down's bars hold an "All other slots" aggregate — slots the chart doesn't draw on
 * their own, so there's more to show.
 *
 * @param {WaterfallBar[] | undefined} bars - The drill-down's bars, or undefined while loading.
 * @return {boolean} True when an aggregate bar is among them.
 */
export function hasOtherSlotsBar(bars: WaterfallBar[] | undefined): boolean {
  return bars?.some((bar) => bar.key === OTHER_SLOTS_KEY) ?? false;
}

/**
 * Whether a slot list has a page after this one: the pages up to it hold fewer slots than the
 * list's `totalCount`. The one check both the list's paging and a reveal's read of it use.
 *
 * @param {SlotRowsPageResponse} page - A page of the list.
 * @return {boolean} True when another page follows it.
 */
export function hasPageAfter(page: SlotRowsPageResponse): boolean {
  return page.page * page.pageSize < page.totalCount;
}

/**
 * Whether a waterfall's bars are slots — a slot drill-down, whose x-axis then carries well names:
 * any bar is a slot ({@link isSlotKey}) or the slots' aggregate ({@link OTHER_SLOTS_KEY}).
 *
 * @param {{ key: string }[]} bars - The waterfall's bars (or columns).
 * @return {boolean} True when any bar is a slot or the slots' aggregate.
 */
export function isSlotWaterfall(bars: { key: string }[]): boolean {
  return bars.some((bar) => isSlotKey(bar.key) || bar.key === OTHER_SLOTS_KEY);
}

/**
 * A slot drill-down with `revealedCount` more slots drawn than its capped chart. The capped chart
 * shows the biggest movers; once anything is revealed the chart reads the slot list highest to
 * lowest instead (`pages` are that list, in value order): the capped chart's two anchors around the
 * list's first slots (the capped chart's own count plus `revealedCount`), then one "All other slots
 * (n)" bar holding the rest — all lower than the slots drawn, so it belongs last — so the bars add
 * up to the closing anchor at every step, and each reveal adds the next slots at the end. The total
 * comes from the capped chart (its top slots plus its aggregate). The chart and the list both leave
 * out the slots that didn't change unless the page shows them, so every slot listed is one to draw.
 * Pure.
 *
 * Nothing revealed — or nothing of the list loaded yet — is the capped chart as it is (`order`
 * `impact`): complete when it leaves no slot out (no "All other slots" bar), or when the list came
 * back empty with no page left. A revealed chart is complete once every listed slot is drawn, every
 * page in; it then drops the aggregate, unless the slots drawn still fall short of the total (the
 * list shifted between pages), when the shortfall keeps its bar. `needsNextPage` says the next
 * reveal would run past the slots loaded while a page is left — so the caller can fetch it one step
 * early.
 *
 * @param {WaterfallBar[]} capped - The capped drill-down: an anchor, its delta bars, an anchor.
 * @param {SlotRowsPageResponse[]} pages - The slot list's pages loaded so far, highest to lowest.
 * @param {number} revealedCount - How many slots past the capped chart's own to draw.
 * @return {RevealedSlotWaterfall} The bars to draw, and how far through the list they reach.
 */
export function revealSlotWaterfall(
  capped: WaterfallBar[],
  pages: SlotRowsPageResponse[],
  revealedCount: number
): RevealedSlotWaterfall {
  const cappedSlotCount = capped.filter((bar) => isSlotKey(bar.key)).length;
  const rows = toSlotRows(pages);
  const lastPage = pages.at(-1);
  const totalCount = lastPage?.totalCount ?? 0;
  const pageLeft = lastPage ? hasPageAfter(lastPage) : false;
  if (revealedCount === 0 || rows.length === 0 || capped.length < 2) {
    // A list that came back with nothing in it, though the capped chart still leaves slots out (the
    // two were read at different times), has nothing more to show: the capped chart is all there is.
    const listEndedEmpty =
      lastPage !== undefined && !pageLeft && rows.length === 0;
    return {
      bars: capped,
      order: 'impact',
      shownCount: cappedSlotCount,
      cappedSlotCount,
      totalCount,
      complete: !hasOtherSlotsBar(capped) || listEndedEmpty,
      needsNextPage: false,
    };
  }

  const target = cappedSlotCount + revealedCount;
  const drawn = rows.slice(0, target);
  const complete = !pageLeft && drawn.length === rows.length;

  const totalChange = capped
    .slice(1, -1)
    .reduce((sum, bar) => sum + bar.value, 0);
  const residual = totalChange - drawn.reduce((sum, row) => sum + row.delta, 0);
  // A shortfall left once the list has no slot to name (it shifted between pages) goes uncounted.
  const restCount = Math.max(totalCount - drawn.length, 0);
  const restBars: WaterfallBar[] =
    !complete || Math.abs(residual) > SLOT_RESIDUAL_TOLERANCE
      ? [
          {
            key: OTHER_SLOTS_KEY,
            label:
              restCount > 0
                ? `All other slots (${format0(restCount)})`
                : 'All other slots',
            type: 'delta',
            value: residual,
          },
        ]
      : [];
  const slotBars = drawn.map((row): WaterfallBar => ({
    key: row.key,
    label: row.label,
    type: 'delta',
    value: row.delta,
  }));
  return {
    bars: [capped[0], ...slotBars, ...restBars, capped[capped.length - 1]],
    order: 'value',
    shownCount: drawn.length,
    cappedSlotCount,
    totalCount,
    complete,
    needsNextPage: pageLeft && rows.length < target + SLOT_REVEAL_STEP,
  };
}

/**
 * Which slot list a slot level reads: its category's, with or without the $0 slots. Another list
 * opens capped, in a window of its own.
 *
 * @param {string | null} rescat - The category on screen (null = all).
 * @param {boolean} showUnchanged - The $0 slots are shown.
 * @return {string} e.g. `pdp:changed-slots`, `all:every-slot`.
 */
function slotListScope(rescat: string | null, showUnchanged: boolean): string {
  return `${rescat ?? 'all'}:${showUnchanged ? 'every-slot' : 'changed-slots'}`;
}

/**
 * Which window a slot chart's bars belong to: its slot list (see {@link slotListScope}) in the
 * order it's drawn in — so switching from the biggest movers to the list highest to lowest opens
 * on the list's start, while more of the same list keeps the user's view.
 *
 * @param {string | null} rescat - The category on screen (null = all).
 * @param {boolean} showUnchanged - The $0 slots are shown.
 * @param {SlotListOrder} order - The order the bars are drawn in.
 * @return {string} e.g. `all:changed-slots:value`.
 */
export function slotWindowKey(
  rescat: string | null,
  showUnchanged: boolean,
  order: SlotListOrder
): string {
  return `${slotListScope(rescat, showUnchanged)}:${order}`;
}

/**
 * Whether a drill level shows slots: a factor's slots, or Slots Removed / Added at the group level.
 *
 * @param {{ groupKey: string, factorKey: string | null }} drill - The drill level: its group, and
 *   its factor (null at the group's level).
 * @return {boolean} True at a slot level.
 */
function isSlotLevel(drill: {
  groupKey: string;
  factorKey: string | null;
}): boolean {
  return !!drill.factorKey || isSlotCoverageKey(drill.groupKey);
}

/**
 * The key a drill level's revealed slots are kept under: the run (the view outlives a run change),
 * the slot list (see {@link slotListScope}) and the level — a factor's slots, or a group's (Slots
 * Removed / Added).
 *
 * @param {number} runId - The run on screen.
 * @param {{ groupKey: string, factorKey: string | null } | null} drill - The drill level, or null at
 *   the overview.
 * @param {string | null} rescat - The category on screen (null = all).
 * @param {boolean} showUnchanged - The $0 slots are shown.
 * @return {string | null} The key, or null at a level with no slots to show (the overview, or a
 *   group of factors).
 */
export function slotRevealKey(
  runId: number,
  drill: { groupKey: string; factorKey: string | null } | null,
  rescat: string | null,
  showUnchanged: boolean
): string | null {
  if (!drill || !isSlotLevel(drill)) return null;
  const level = drill.factorKey
    ? `factor:${drill.factorKey}`
    : `group:${drill.groupKey}`;
  return `${runId}:${slotListScope(rescat, showUnchanged)}:${level}`;
}

/**
 * What a slot level says in place of its chart when, its $0 slots left out, none of its slots
 * changed — the chart came back as its two anchors with nothing between them. None at any other
 * level, while the $0 slots are shown, while the capped chart loads, or when the level has no
 * breakdown at all (no anchors either), which says so itself.
 *
 * @param {{ groupKey: string, factorKey: string | null } | null} drill - The drill level, or null at
 *   the overview.
 * @param {string | null} rescat - The category on screen (null = all).
 * @param {boolean} showUnchanged - The $0 slots are shown.
 * @param {WaterfallBar[] | undefined} cappedBars - The level's capped chart, once loaded.
 * @return {EmptyNotice | undefined} The notice, or undefined when the chart has slots to draw.
 */
export function noSlotChangedNotice(
  drill: { groupKey: string; factorKey: string | null } | null,
  rescat: string | null,
  showUnchanged: boolean,
  cappedBars: WaterfallBar[] | undefined
): EmptyNotice | undefined {
  if (!drill || !isSlotLevel(drill) || showUnchanged || !cappedBars) {
    return undefined;
  }
  const onlyAnchors =
    cappedBars.length >= 2 &&
    cappedBars.every((bar) => bar.type === 'absolute');
  if (!onlyAnchors) return undefined;
  return {
    title: rescat
      ? `No ${rescatOption(rescat).label} slots changed`
      : 'No slots changed',
    subtitle: `Turn on ${SHOW_UNCHANGED_SLOTS_LABEL} to see any that didn't.`,
  };
}
